import { useField } from 'formik';
import React, { ChangeEventHandler, useMemo } from 'react';

import { CalendarDateService } from '@uplift/services';

import { BaseTextField, BaseTextFieldProps } from '../BaseTextField';
import { MaskedInputComponent } from './masked-input-component';
import { MaskedInputType } from './masked-input.types';

export type MaskedInputProps = BaseTextFieldProps & {
  name: string;
  type?: MaskedInputType;
  mask?: string;
  showPlaceholder?: boolean;
  placeholderChar?: string;
};

const inputPropsByType: Partial<Record<MaskedInputType, BaseTextFieldProps['inputProps']>> = {
  card: {
    inputMode: 'numeric'
  },
  cardExpiration: {
    inputMode: 'numeric'
  },
  phone: {
    inputMode: 'tel'
  },
  zip: {
    inputMode: 'numeric'
  },
  weight: {
    inputMode: 'numeric',
  },
  dob: {
    inputMode: 'numeric',
  },
  cvc: {
    inputMode: 'numeric',
  },
  cvc3: {
    inputMode: 'numeric',
  },
  licenseExpiration: {
    inputMode: 'numeric'
  }
};

export const MaskedInput: React.FC<MaskedInputProps> = ({
  name,
  type,
  helperText,
  mask,
  showPlaceholder,
  placeholderChar,
  slotProps,
  ...props
}) => {
  const [ field, meta, helpers ] = useField(name);
  const fieldValue = React.useMemo(() => {
    if (field.value instanceof Date) {
      return CalendarDateService.date(field.value).utc().format('MM/DD/YYYY');
    }

    if (CalendarDateService.isDateObject(field.value)) {
      return field.value.format('MM/DD/YYYY');
    }

    return field.value;
  }, [ field.value ]);

  const onChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    helpers.setValue(e.target.value);
  };

  const extendedInputProps = useMemo(() => ({
    ...(type ? inputPropsByType[type] : {}),
    ...slotProps?.htmlInput,
    mask,
    showPlaceholder,
    placeholderChar
  }), [ type, slotProps, mask, showPlaceholder, placeholderChar ]);

  return (
    <BaseTextField
      {...field}
      type={type}
      name={name}
      value={fieldValue}
      onChange={onChange}
      error={meta.touched && !!meta.error}
      helperText={helperText}
      errorHelperText={meta.touched && meta.error}
      slotProps={{
        ...slotProps,
        htmlInput: {
          ...props.inputProps,
          ...slotProps?.htmlInput,
          ...extendedInputProps
        },
        input: {
          ...props.InputProps,
          ...slotProps?.input,
          inputComponent: MaskedInputComponent,
        }
      }}
      {...props}
    />
  );
};
